import classNames from "classnames";
import React, { use, useId } from "react";
import {
  Controller,
  FieldErrors,
  FieldName,
  FieldValues,
  Path,
  useFormContext,
} from "react-hook-form";
import Error from "../Error";
import Label from "./Label";
import { HiOutlineEye } from "react-icons/hi";
import { HiOutlineEyeOff } from "react-icons/hi";
import {
  ErrorMessage,
  FieldValuesFromFieldErrors,
} from "@hookform/error-message";

type props<t extends FieldValues> = {
  name: Path<t>;
  label: string;
  optional?: boolean;
  autoComplete?: "new-password" | "current-password";
} & React.InputHTMLAttributes<HTMLInputElement>;

function PasswordInput<t extends FieldValues>({
  name,
  label,
  optional,
  className,
  autoComplete,
  ...otherProps
}: props<t>) {
  const { control, formState } = useFormContext<t>();
  const [showPassword, setShowPassword] = React.useState(false);
  const id = useId();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, onBlur, value, ref } }) => {
        return (
          <div className={classNames("", className)}>
            <Label htmlFor={id} optional={optional}>
              {label}
            </Label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={value}
                name={name}
                onChange={onChange}
                className="appearance-none block w-full bg-gray-100 border border-gray-100 rounded py-3 px-4 leading-tight focus:outline-hidden focus:bg-white focus:border-gray-500"
                autoComplete={autoComplete}
                id={id}
                {...otherProps}
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                type="button"
                tabIndex={-1}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 focus:outline-hidden"
              >
                {showPassword ? (
                  <HiOutlineEyeOff className="h-6 w-auto" />
                ) : (
                  <HiOutlineEye className="h-6 w-auto" />
                )}
              </button>
            </div>
            <ErrorMessage
              errors={formState.errors}
              name={
                name as FieldName<FieldValuesFromFieldErrors<FieldErrors<t>>>
              }
              render={({ message }) => <Error>{message}</Error>}
            />
          </div>
        );
      }}
    />
  );
}

export default PasswordInput;
